/**
 * FK: カートの配送希望日・時間帯（snippets/fk-delivery-date.liquid）。
 * - 日付の選択肢は日本時間の「今日」から組み立てる（ページはキャッシュされうるので Liquid の now は使わない）。
 * - 選んだ時点で /cart/update.js に attributes として保存する（カート画面を離れても値が残る）。
 *   チェックアウトの送信にはフォームの項目として同じ値が乗るので、保存の通信が間に合わなくても取りこぼさない。
 * - 時間帯だけの状態・最短日より前の日付が残っていれば、保存済みの日付を消す。
 */
(() => {
  if (customElements.get('fk-delivery-date')) return;

  const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
  const DATE_KEY = '配送希望日';
  // 時間帯だけ指定できる商品を含むときに配送希望日へ入れる値（メール・注文一覧・出荷 CSV でそのまま読める）
  const TIME_ONLY_VALUE = '指定不可';

  /** 日本時間の今日（年・月・日）を UTC の 0 時として返す */
  function todayInJapan() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    return new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
  }

  /** 2026/10/08(木) */
  function label(d) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}/${m}/${day}(${WEEKDAYS[d.getUTCDay()]})`;
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async function saveAttributes(attributes) {
    const res = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error(`cart/update ${res.status}`);
    return res.json();
  }

  class FkDeliveryDate extends HTMLElement {
    // カートが変わるとテーマが DOM を差分で書き換える（assets/morph.js）。要素は残るので、
    // 時間帯だけの状態・保存済みの日付の変化は属性の変化として受けて組み直す
    static get observedAttributes() {
      return ['data-time-only', 'data-current-date'];
    }

    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        // 日付の select がサーバーの描画で入れ替わったら（＝組み立て済みの印が無い）組み直す
        new MutationObserver(() => {
          const select = this.querySelector(`select[data-fk-key="${DATE_KEY}"]`);
          if (select && !select.dataset.fkBuilt) this.refresh();
        }).observe(this, { childList: true, subtree: true });
        this.addEventListener('change', (event) => {
          const select = event.target;
          if (!(select instanceof HTMLSelectElement) || !select.dataset.fkKey) return;
          this.save({ [select.dataset.fkKey]: select.value });
        });
      }
      this.refresh();
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      // 属性は子より先に書き換わるので、子（日付の select）が入れ替わってから組み直す
      if (this.isConnected && oldValue !== newValue) requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      const timeOnly = this.dataset.timeOnly === 'true';
      const current = this.dataset.currentDate || '';
      if (timeOnly) {
        // 時間帯だけの状態＝配送希望日は「指定不可」に揃える（保存済みの日付も上書きされる）
        if (current !== TIME_ONLY_VALUE) {
          this.dataset.currentDate = TIME_ONLY_VALUE;
          this.save({ [DATE_KEY]: TIME_ONLY_VALUE }, true);
        }
        return;
      }

      // 選べない日付（範囲外・「指定不可」の残り）が残っていれば消す
      const valid = this.buildDateOptions();
      if (current && !valid.includes(current)) {
        this.dataset.currentDate = '';
        this.save({ [DATE_KEY]: '' }, true);
      }
    }

    /** いま選べる日付の一覧（日本時間の今日から計算し直す） */
    validDates() {
      const minDays = Number(this.dataset.minDays) || 3;
      const rangeDays = Number(this.dataset.rangeDays) || 14;
      const base = todayInJapan();
      const values = [];
      for (let i = 0; i < rangeDays; i++) {
        const d = new Date(base);
        d.setUTCDate(base.getUTCDate() + minDays + i);
        values.push(label(d));
      }
      return values;
    }

    /** 日付の選択肢を入れ直し、選べる値の一覧を返す */
    buildDateOptions() {
      const select = this.querySelector(`select[data-fk-key="${DATE_KEY}"]`);
      if (!select) return [];
      const current = this.dataset.currentDate || '';
      const values = this.validDates();

      select.dataset.fkBuilt = 'true';
      select.replaceChildren(new Option('指定なし', ''));
      values.forEach((v) => select.add(new Option(v, v, false, v === current)));
      return values;
    }

    /**
     * チェックアウトの直前に呼ぶ。選ばれている日付が今日の時点で範囲外なら、消して止める。
     * @returns {boolean} 進んでよければ true
     */
    checkBeforeCheckout() {
      const select = this.querySelector(`select[data-fk-key="${DATE_KEY}"]`);
      if (!select || !select.value || this.validDates().includes(select.value)) return true;

      this.dataset.currentDate = ''; // 選択肢の組み直しは attributeChangedCallback が行う
      this.save({ [DATE_KEY]: '' }, true);
      const status = this.querySelector('.fk-delivery__status');
      if (status) {
        status.textContent = 'お届け希望日が指定できる範囲を過ぎました。日付を選び直してください。';
        status.classList.add('fk-delivery__status--error');
      }
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      select.focus({ preventScroll: true });
      return false;
    }

    async save(attributes, silent = false) {
      const status = this.querySelector('.fk-delivery__status');
      if (!silent && status) {
        status.classList.remove('fk-delivery__status--error');
        status.textContent = '保存しています…';
      }
      try {
        await saveAttributes(attributes);
        if (DATE_KEY in attributes) this.dataset.currentDate = attributes[DATE_KEY];
        if (!silent && status) status.textContent = '保存しました';
      } catch (e) {
        if (status) status.textContent = '保存できませんでした。もう一度お選びください。';
        console.error('[fk-delivery-date]', e);
      }
    }
  }

  customElements.define('fk-delivery-date', FkDeliveryDate);

  // チェックアウト（カートのフォームの送信）を捕まえ、日付をその時点で判定し直す。
  // カート画面を開いたまま日付をまたいだ場合でも、範囲外の日付のまま決済へ進ませない
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
      if (event.submitter && event.submitter.name !== 'checkout') return;
      const hosts = [...document.querySelectorAll('fk-delivery-date')];
      if (hosts.every((host) => host.checkBeforeCheckout())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
})();
